import {
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgTemplateOutlet
} from "@angular/common";
import {
  Component,
  HostListener,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import {
  LgFocusDirective,
  LgGridColDirective,
  LgGridContainerDirective,
  LgGridRowDirective,
  LgIconComponent
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { filter, Observable } from "rxjs";
import { AnalyticsService } from "../../../services/analytics/analytics.service";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import {
  setMainNavMobileOpen,
  setMainNavOpen
} from "../../../store/global/global.actions";
import { GlobalState } from "../../../store/global/global.reducer";
import {
  selectActiveLanguage,
  selectBaseHref,
  selectMainMobileNavOpen,
  selectPageTitle
} from "../../../store/global/global.selectors";
import { SearchState } from "../../../store/search/search.reducer";
import { selectSearchCollectionName } from "../../../store/search/search.selectors";
import { DynamicComponentItem } from "../../utils/dynamic-loader/dynamic-component-item";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { HeaderLinksComponent } from "../HeaderLinks/HeaderLinks.component";
import { LinkComponent } from "../Link/Link.component";
import { LinkCheck } from "../../../services/link-check/link-check.service";

@Component({
  selector: "dotcom-frontend-navigation",
  templateUrl: "./Navigation.component.html",
  styleUrl: "./Navigation.component.scss",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    LgGridRowDirective,
    LgGridContainerDirective,
    LgGridColDirective,
    LgIconComponent,
    LoaderComponent,
    LinkComponent,
    HeaderLinksComponent,
    NgClass,
    LgFocusDirective,
    NgTemplateOutlet
  ]
})
export class NavigationComponent implements OnInit {
  @Input() data: any | undefined;
  headerData: any | undefined;
  activeMainNav = -1;
  activeSecondaryNav = -1;
  mobileNav = false;
  baseHref = "";
  pageTitle = "";
  contentArea: any;
  navStart: Observable<NavigationStart>;
  collectionName = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  baseHref$: Observable<string> = this.store.select(selectBaseHref);
  collectionName$: Observable<string> = this.store.select(
    selectSearchCollectionName
  );
  isMobileNavOpen$: Observable<boolean> = this.store.select(
    selectMainMobileNavOpen
  );
  searchComponent: DynamicComponentItem | undefined;
  searchMobileComponent: DynamicComponentItem | undefined;
  mobileMenuLabel: string | undefined;
  progressiveRevealNavigationOnDesktop = false;
  openedMenus: any = [];
  showRootNav = true;
  backButtonLabel = "Back";
  isMobile: boolean | undefined;
  isBrowser = isPlatformBrowser(this.platformId);
  activeItems: boolean[] = [];
  mainItemFocus: number | undefined;
  hasIntroTitle = false;
  isLanguageMenu = false;
  currentLanguage = { lang: "en", name: "English" };
  activeLanguage$: Observable<{ lang: string, name: string }> =
    this.store.select(selectActiveLanguage);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public router: Router,
    private store: Store<{ global: GlobalState; search: SearchState }>,
    private componentLoaderService: ComponentLoaderService,
    private analyticsService: AnalyticsService,
    private linkCheck: LinkCheck,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });

    this.baseHref$.subscribe((baseHref) => {
      this.baseHref = baseHref;
    });

    this.collectionName$.subscribe((name) => {
      this.collectionName = name;
    });

    this.isMobileNavOpen$.subscribe((open) => {
      this.mobileNav = open;
    });

    this.activeLanguage$.subscribe((language) => {
      this.currentLanguage = language;
    });

    this.navStart = router.events.pipe(
      filter((evt) => evt instanceof NavigationStart)
    ) as Observable<NavigationStart>;
  }

  async ngOnInit() {
    if (this.data) {
      this.checkIfTablet();
      this.headerData = this.data["headerData"];

      this.searchMobileComponent =
        await this.componentLoaderService.loadComponent("Search", "globals", {
          searchTitle: this.headerData.searchTitle,
          searchPlaceholderText: this.headerData.searchPlaceholderText,
          showInNavigation: true
        });

      this.searchComponent = await this.componentLoaderService.loadComponent(
        "Search",
        "globals",
        {
          searchTitle: this.headerData.searchTitle,
          searchPlaceholderText: this.headerData.searchPlaceholderText
        }
      );

      if (this.data["navData"].expanded?.children) {
        this.contentArea = [];
        for (const mainNavItem of this.data["navData"].expanded.children) {
          if (mainNavItem.expanded.introTitle) {
            this.hasIntroTitle = true;
          }
          this.activeItems.push(false);
          if (mainNavItem.expanded.promoBlock) {
            const promoBlock =
              await this.componentLoaderService.contentAreaLoader(
                mainNavItem.expanded.promoBlock
              );
            this.contentArea.push(promoBlock);
          } else {
            this.contentArea.push(null);
          }
        }
      }

      this.mobileMenuLabel = this.data["navData"].expanded?.mobileMenuLabel;

      if (this.data["navData"].expanded?.progressiveRevealNavigationOnDesktop) {
        this.progressiveRevealNavigationOnDesktop = true;
      }

      if (this.data["navData"].expanded?.backButtonLabel) {
        this.backButtonLabel = this.data["navData"].expanded?.backButtonLabel;
      }
    }

    this.navStart.subscribe(() => {
      this.activeMainNav = -1;
      this.activeSecondaryNav = -1;
      this.store.dispatch(setMainNavMobileOpen({ open: false }));
      this.unLockBodyScroll();
    });

  }

  interactionClickEvent(clickUrl: any, clickName: any, containerRegion: any) {
    this.analyticsService.adobeInteractionEvent(
      "Main Navigation Block",
      clickUrl,
      clickName,
      containerRegion,
      "NavClick",
      this.pageTitle,
      ""
    );
  }

  toggleMainNav(event: any, menu: number) {
    event.stopPropagation();

    if (this.activeMainNav === menu) {
      this.activeMainNav = -1;
      this.unLockBodyScroll();
      this.store.dispatch(setMainNavOpen({ open: false }));
      this.mainItemFocus = menu;
    } else {
      this.activeMainNav = menu;
      this.lockBodyScroll();
      this.store.dispatch(setMainNavOpen({ open: true }));
    }

    this.openedMenus = [];
    this.showRootNav = true;
  }

  toggleSecondaryNav(event: any, menu: number) {
    event.stopPropagation();

    if (this.activeSecondaryNav === menu) {
      this.activeSecondaryNav = -1;
    } else {
      this.activeSecondaryNav = menu;
    }
  }

  toggleMobileNav(event: any) {
    event.stopPropagation();

    if (this.mobileNav) {
      this.store.dispatch(setMainNavMobileOpen({ open: false }));
      this.activeMainNav = this.activeSecondaryNav = -1;
      this.unLockBodyScroll();
    } else {
      this.store.dispatch(setMainNavMobileOpen({ open: true }));
      this.lockBodyScroll();
    }
  }

  toggleLanguages() {
    this.isLanguageMenu = !this.isLanguageMenu;
  }

  focusMainItem(item: number) {
    this.mainItemFocus = item;
  }

  lockBodyScroll() {
    this.document.body.classList.add("scroll-lock");
  }

  unLockBodyScroll() {
    this.document.body.classList.remove("scroll-lock");
  }

  isActiveRoute(url: string, children: any, index: number) {
    this.activeItems[index] = false;
    const isHomepage = !this.router.parseUrl(this.router.url).root.children[
      "primary"
    ];
    if (isHomepage) {
      return url === "/";
    } else {
      if (children) {
        if (url === this.router.url) {
          return true;
        }

        this.isActiveRouteChildMenu(children, this.router.url, index);
        return this.activeItems[index];
      } else {
        return url === this.router.url;
      }
    }
  }

  isActiveRouteChildMenu(data: any, url: string, index: number) {
    for (const item of data) {
      if (item.expanded.targetUrl) {
        let targetUrl;
        try {
          targetUrl = new URL(item.expanded.targetUrl);
        } catch {
          console.error("INVALID URL");
        }
        if (targetUrl) {
          if (url === targetUrl.pathname) {
            this.activeItems[index] = true;
          }
        } else if (item.expanded.children) {
          this.isActiveRouteChildMenu(item.expanded.children, url, index);
        }
      }
    }
  }

  isExternalLink(url: string) {
    return !url?.startsWith(this.baseHref);
  }

  formatUrl(url: string) {
    if (!this.isExternalLink(url)) {
      return url.replace(this.baseHref, "");
    } else {
      return url;
    }
  }

  promoColumnOffset(colNo: number) {
    const offset = (3 - colNo) * 3;

    return offset > 0 ? offset : 0;
  }

  getColumns(data: any) {
    return data.children.slice(0, data.promoBlock ? 3 : 4);
  }

  openSubMenu(level: number, id: number, parentId: number) {
    if (this.isActive(id)) {
      this.openedMenus = this.openedMenus.slice(0, level);
    } else {
      this.openedMenus = this.openedMenus.filter(
        (item: any) => item.level < level
      );

      this.openedMenus.push({ level: level, activeId: id });

      if (this.isMobile) {
        this.showRootNav = false;
      } else {
        this.showRootNav = level >= 0;
      }
    }

    const scrollBar = this.document.getElementById(
      `scrollContainer-${parentId}`
    );
    if (scrollBar && (level > 1 || this.isMobile)) {
      setTimeout(() => {
        scrollBar.scrollLeft = scrollBar.scrollWidth;
      }, 0);
    }
  }

  isActive(menuId: number) {
    return this.openedMenus.map((item: any) => item.activeId).includes(menuId);
  }

  progressiveNavigateBack() {
    this.openedMenus.splice(-1);

    if (this.isMobile) {
      this.showRootNav = this.openedMenus.length === 0;
    }
  }

  displayBackButton() {
    if (this.isMobile) {
      return this.openedMenus.length >= 1;
    } else {
      return this.openedMenus.length >= 3;
    }
  }

  showPromoBox() {
    return this.openedMenus.length < 2;
  }

  promoBoxOffset() {
    return this.openedMenus.length >= 1 ? 0 : 4;
  }

  checkIfTablet() {
    if (this.isBrowser) {
      this.isMobile = window.innerWidth < 1024;
    }
  }

  checkLinkAvailability(url: string) {
    return this.linkCheck.linkUnavailableCheck(url);
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    const currentView = this.isMobile;
    this.checkIfTablet();
    if (this.isMobile !== currentView) {
      this.openedMenus = [];
      this.showRootNav = true;
    }
  }
}
